/**
 * Pure spatial A/V volume rules for the HQ map.
 * Used by the world engine gain curve and unit tests.
 *
 * Rules:
 * - Open floor: distance falloff within radius tiles
 * - Private room (boardroom, huddle, personal office): same room = full volume;
 *   different private room / outside = mute
 * - Focus vs non-focus: soft attenuate when exactly one side is in focus
 */

export type VolumeZoneKind = {
  id: string;
  /** Hard A/V isolation bucket (meeting, huddle_*, office_N). */
  privateKey: string | null;
  /** Soft focus attenuation. */
  isFocus: boolean;
};

export type SpatialVolumeInput = {
  self: { fx: number; fy: number; zone: VolumeZoneKind };
  peer: { fx: number; fy: number; zone: VolumeZoneKind };
  /** Distance falloff radius in tiles (open floor only). */
  radius: number;
};

/**
 * Returns 0..1 gain for hearing `peer` from `self`.
 * Same private room → 1 (hear everyone in the room).
 */
export function computeSpatialVolume(input: SpatialVolumeInput): number {
  const { self, peer, radius } = input;
  const pk = self.zone.privateKey;
  const bk = peer.zone.privateKey;

  if (pk || bk) {
    // Different isolation buckets (or only one side private) → hard mute.
    if (pk !== bk) return 0;
    // Same private room / personal office — full presence for the whole room.
    return 1;
  }

  const r = radius > 0 ? radius : 7;
  const d = Math.hypot(peer.fx - self.fx, peer.fy - self.fy);
  let v = Math.max(0, Math.min(1, 1 - d / r));
  if (self.zone.isFocus !== peer.zone.isFocus) v *= 0.25;
  return v;
}
